import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Image,
  Input,
  SimpleGrid,
  Spacer,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FiCheckCircle, FiClipboard, FiCompass, FiShield, FiBarChart2 } from 'react-icons/fi';
import { TbArrowBigRightLines } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import Header from '../site/components/Header';
import { useUser } from '../contexts/UserContext';
import publicAxios from '../publicAxios';
import { useTourSession } from '../tour/useTourSession';

const defaultToolKey = 'school_mapping';

const isYouTubeUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();
    return hostname.includes('youtube.com') || hostname === 'youtu.be' || hostname.endsWith('.youtu.be');
  } catch (error) {
    return false;
  }
};

const parseYouTubeStartTime = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;

  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  if (/^\d+s$/.test(trimmed)) return Number(trimmed.slice(0, -1));

  const match = trimmed.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
  if (!match) return null;

  const hours = Number(match[1] || 0);
  const minutes = Number(match[2] || 0);
  const seconds = Number(match[3] || 0);
  const total = hours * 3600 + minutes * 60 + seconds;
  return total > 0 ? total : null;
};

const toYouTubeEmbedUrl = (url) => {
  if (!isYouTubeUrl(url)) return null;

  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();
    const pathParts = parsed.pathname.split('/').filter(Boolean);
    let videoId = null;

    if (hostname === 'youtu.be' || hostname.endsWith('.youtu.be')) {
      videoId = pathParts[0] || null;
    } else if (parsed.pathname === '/watch') {
      videoId = parsed.searchParams.get('v');
    } else if (pathParts[0] === 'embed' && pathParts[1]) {
      videoId = pathParts[1];
    } else if (pathParts[0] === 'shorts' && pathParts[1]) {
      videoId = pathParts[1];
    }

    if (!videoId) return null;

    const hashParams = new URLSearchParams((parsed.hash || '').replace(/^#/, ''));
    const rawStart = parsed.searchParams.get('start') || parsed.searchParams.get('t') || hashParams.get('t');
    const start = parseYouTubeStartTime(rawStart);

    const embedUrl = new URL(`https://www.youtube.com/embed/${videoId}`);
    if (start && start > 0) {
      embedUrl.searchParams.set('start', String(start));
    }

    return embedUrl.toString();
  } catch (error) {
    return null;
  }
};

const resolveMedia = (configMediaOrImage) => {
  if (!configMediaOrImage || typeof configMediaOrImage !== 'object') return null;
  const src = typeof configMediaOrImage.src === 'string' ? configMediaOrImage.src.trim() : '';
  if (!src) return null;

  const inferredType = isYouTubeUrl(src) ? 'video' : 'image';
  const type = configMediaOrImage.type === 'video' || configMediaOrImage.type === 'image' ? configMediaOrImage.type : inferredType;
  const embedUrl = type === 'video' ? toYouTubeEmbedUrl(src) : null;

  return {
    type,
    src,
    altKey: configMediaOrImage.altKey,
    embedUrl,
  };
};

const landingToolConfig = {
  school_mapping: {
    theme: {
      accent: 'blue',
      gradient: 'linear(to-br, blue.50, white)',
      highlightGradient: 'linear(to-br, white, blue.50)',
      borderColor: 'blue.100',
      buttonColorScheme: 'blue',
      badgeColor: 'blue.700',
    },
    hero: {
      titleKey: 'publicLead.tools.school_mapping.hero.title',
      subtitleKey: 'publicLead.tools.school_mapping.hero.subtitle',
      introHtmlKey: 'publicLead.tools.school_mapping.hero.introHtml',
      primaryCtaKey: 'publicLead.ui.requestConsultation',
      secondaryCtaKey: 'publicLead.ui.startTour',
    },
    howItWorks: {
      headingKey: 'publicLead.tools.school_mapping.howItWorks.heading',
      bulletKeys: [
        'publicLead.tools.school_mapping.howItWorks.bullets.0',
        'publicLead.tools.school_mapping.howItWorks.bullets.1',
        'publicLead.tools.school_mapping.howItWorks.bullets.2',
      ],
      footerEmphasisKey: 'publicLead.tools.school_mapping.howItWorks.footerEmphasis',
      image: {
        src: '/images/landing/consultant-landing-how-it-works.jpg',
        altKey: 'publicLead.tools.school_mapping.howItWorks.imageAlt',
      },
    },
    valueStrip: [
      { labelKey: 'publicLead.tools.school_mapping.valueStrip.0' },
      { labelKey: 'publicLead.tools.school_mapping.valueStrip.1' },
      { labelKey: 'publicLead.tools.school_mapping.valueStrip.2' },
    ],
    toolSection: {
      headingKey: 'publicLead.tools.school_mapping.toolSection.heading',
      descriptionHtmlKey: 'publicLead.tools.school_mapping.toolSection.descriptionHtml',
      bulletKeys: [
        'publicLead.tools.school_mapping.toolSection.bullets.0',
        'publicLead.tools.school_mapping.toolSection.bullets.1',
        'publicLead.tools.school_mapping.toolSection.bullets.2',
      ],
      subheadingKey: 'publicLead.tools.school_mapping.toolSection.subheading',
      subBulletKeys: [
        'publicLead.tools.school_mapping.toolSection.subBullets.0',
        'publicLead.tools.school_mapping.toolSection.subBullets.1',
        'publicLead.tools.school_mapping.toolSection.subBullets.2',
        'publicLead.tools.school_mapping.toolSection.subBullets.3',
      ],
      summaryKey: 'publicLead.tools.school_mapping.toolSection.summary',
      media: {
        type: 'video',
        src: 'https://www.youtube.com/watch?v=6kaF6EyqQCk',
      },
    },
    why: {
      headingKey: 'publicLead.tools.school_mapping.why.heading',
      introKey: 'publicLead.tools.school_mapping.why.intro',
      bulletKeys: [
        'publicLead.tools.school_mapping.why.bullets.0',
        'publicLead.tools.school_mapping.why.bullets.1',
        'publicLead.tools.school_mapping.why.bullets.2',
        'publicLead.tools.school_mapping.why.bullets.3',
        'publicLead.tools.school_mapping.why.bullets.4',
      ],
      summaryKey: 'publicLead.tools.school_mapping.why.summary',
    },
    testimonial: {
      quoteKey: 'publicLead.tools.school_mapping.testimonial.quote',
      attributionKey: 'publicLead.tools.school_mapping.testimonial.attribution',
    },
    leadForm: {
      finalCtaTitleKey: 'publicLead.tools.school_mapping.leadForm.finalCtaTitle',
      finalCtaBodyKey: 'publicLead.tools.school_mapping.leadForm.finalCtaBody',
      footerLineKey: 'publicLead.tools.school_mapping.leadForm.footerLine',
    },
  },
  team_survey: {
    theme: {
      accent: 'green.600',
      gradient: 'linear(to-br, green.50, white)',
      highlightGradient: 'linear(to-br, white, green.50)',
      borderColor: 'green.100',
      buttonColorScheme: 'green',
      badgeColor: 'green.700',
    },
    hero: {
      titleKey: 'publicLead.tools.team_survey.hero.title',
      subtitleKey: 'publicLead.tools.team_survey.hero.subtitle',
      introHtmlKey: 'publicLead.tools.team_survey.hero.introHtml',
      primaryCtaKey: 'publicLead.ui.requestConsultation',
      secondaryCtaKey: 'publicLead.ui.startTour',
    },
    howItWorks: {
      headingKey: 'publicLead.tools.team_survey.howItWorks.heading',
      bulletKeys: [
        'publicLead.tools.team_survey.howItWorks.bullets.0',
        'publicLead.tools.team_survey.howItWorks.bullets.1',
        'publicLead.tools.team_survey.howItWorks.bullets.2',
      ],
      footerEmphasisKey: 'publicLead.tools.team_survey.howItWorks.footerEmphasis',
      image: {
        src: '/images/landing/consultant-landing-hero-school.jpg',
        altKey: 'publicLead.tools.team_survey.howItWorks.imageAlt',
      },
    },
    valueStrip: [
      { labelKey: 'publicLead.tools.team_survey.valueStrip.0' },
      { labelKey: 'publicLead.tools.team_survey.valueStrip.1' },
      { labelKey: 'publicLead.tools.team_survey.valueStrip.2' },
    ],
    toolSection: {
      headingKey: 'publicLead.tools.team_survey.toolSection.heading',
      descriptionHtmlKey: 'publicLead.tools.team_survey.toolSection.descriptionHtml',
      bulletKeys: [
        'publicLead.tools.team_survey.toolSection.bullets.0',
        'publicLead.tools.team_survey.toolSection.bullets.1',
        'publicLead.tools.team_survey.toolSection.bullets.2',
        'publicLead.tools.team_survey.toolSection.bullets.3',
      ],
      subheadingKey: 'publicLead.tools.team_survey.toolSection.subheading',
      subBulletKeys: [
        'publicLead.tools.team_survey.toolSection.subBullets.0',
        'publicLead.tools.team_survey.toolSection.subBullets.1',
        'publicLead.tools.team_survey.toolSection.subBullets.2',
      ],
      summaryKey: 'publicLead.tools.team_survey.toolSection.summary',
      media: {
        type: 'image',
        src: '/images/landing/consultant-landing-tool-team-survey.jpg',
        altKey: 'publicLead.tools.team_survey.toolSection.imageAlt',
      },
    },
    why: {
      headingKey: 'publicLead.tools.team_survey.why.heading',
      introKey: 'publicLead.tools.team_survey.why.intro',
      bulletKeys: [
        'publicLead.tools.team_survey.why.bullets.0',
        'publicLead.tools.team_survey.why.bullets.1',
        'publicLead.tools.team_survey.why.bullets.2',
        'publicLead.tools.team_survey.why.bullets.3',
      ],
      summaryKey: 'publicLead.tools.team_survey.why.summary',
    },
    testimonial: {
      quoteKey: 'publicLead.tools.team_survey.testimonial.quote',
      attributionKey: 'publicLead.tools.team_survey.testimonial.attribution',
    },
    leadForm: {
      finalCtaTitleKey: 'publicLead.tools.team_survey.leadForm.finalCtaTitle',
      finalCtaBodyKey: 'publicLead.tools.team_survey.leadForm.finalCtaBody',
      footerLineKey: 'publicLead.tools.team_survey.leadForm.footerLine',
    },
  },
};

const normalizeToolKey = (value) => {
  if (!value || typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase().replace(/-/g, '_');
  return normalized || null;
};

const PublicConsultantLead = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { consultantSlug, advisorSlug, slug } = useParams();
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const userContext = useUser();
  const { applyUserState, clearUserSession } = userContext || {};
  const { establishTourSession } = useTourSession({
    applyUserState,
    clearUserSession,
    toast,
  });

  const [isHowImageError, setIsHowImageError] = useState(false);
  const [isToolImageError, setIsToolImageError] = useState(false);
  const [isToolVideoError, setIsToolVideoError] = useState(false);

  const [resolvedConsultant, setResolvedConsultant] = useState(null);
  const [resolvedAdvisor, setResolvedAdvisor] = useState(null);
  const [isLoadingConsultant, setIsLoadingConsultant] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStartingTour, setIsStartingTour] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [hasSubmittedLead, setHasSubmittedLead] = useState(false);

  const leadFormRef = useRef(null);
  const successAlertRef = useRef(null);
  const submitLeadInFlightRef = useRef(false);
  const startTourInFlightRef = useRef(false);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    organization_name: '',
    role: '',
  });

  const lookupSlug = advisorSlug || consultantSlug || slug || null;

  const requestedToolKey = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return normalizeToolKey(searchParams.get('tool_key') || searchParams.get('tool'));
  }, [location.search]);

  const activeToolKey = requestedToolKey && landingToolConfig[requestedToolKey] ? requestedToolKey : null;
  const activeToolConfig = useMemo(
    () => (activeToolKey ? landingToolConfig[activeToolKey] : landingToolConfig[defaultToolKey]),
    [activeToolKey],
  );
  const activeTheme = activeToolConfig.theme;
  const currentLang = i18n.language === 'sv' ? 'sv' : 'en';
  const tt = (key, fallback, options = {}) =>
    t(key, {
      lng: currentLang,
      ...(fallback !== undefined ? { defaultValue: fallback } : {}),
      ...options,
    });
  const fallbackConsultantName = tt('publicLead.ui.fallbackConsultant', 'HKE Team');
  const toolSectionMedia = useMemo(
    () => resolveMedia(activeToolConfig.toolSection.media || activeToolConfig.toolSection.image),
    [activeToolConfig],
  );

  useEffect(() => {
    setIsHowImageError(false);
    setIsToolImageError(false);
    setIsToolVideoError(false);
  }, [activeToolKey]);

  useEffect(() => {
    let isMounted = true;

    const loadConsultant = async () => {
      if (!lookupSlug) {
        setResolvedAdvisor(null);
        setResolvedConsultant(null);
        return;
      }

      setIsLoadingConsultant(true);
      try {
        const response = await publicAxios.get(`/public/consultants/${lookupSlug}`);
        if (!isMounted) return;
        const advisor = response?.data?.advisor || null;
        const consultant = response?.data?.consultant || null;
        setResolvedAdvisor(advisor);
        setResolvedConsultant(consultant);
      } catch (error) {
        if (!isMounted) return;
        setResolvedAdvisor(null);
        setResolvedConsultant(null);
      } finally {
        if (isMounted) {
          setIsLoadingConsultant(false);
        }
      }
    };

    loadConsultant();

    return () => {
      isMounted = false;
    };
  }, [lookupSlug]);

  const advisorName = resolvedAdvisor?.name || null;
  const consultantName = resolvedConsultant?.name || null;

  const titleName = useMemo(() => {
    if (isLoadingConsultant && lookupSlug) return fallbackConsultantName;
    if (advisorName) return advisorName;
    if (consultantName) return consultantName;
    return fallbackConsultantName;
  }, [advisorName, consultantName, isLoadingConsultant, lookupSlug]);

  const referredByText = useMemo(() => {
    if (advisorName) return advisorName;
    if (consultantName) return consultantName;
    return null;
  }, [advisorName, consultantName]);

  const contactDetailText = useMemo(() => {
    if (advisorName && consultantName) return `${advisorName} · ${consultantName}`;
    if (advisorName) return advisorName;
    if (consultantName) return consultantName;
    return fallbackConsultantName;
  }, [advisorName, consultantName]);

  const advisorBookingUrl = useMemo(() => {
    const url = resolvedAdvisor?.booking_url;
    return typeof url === 'string' ? url.trim() : '';
  }, [resolvedAdvisor]);

  const consultantBookingUrl = useMemo(() => {
    const url = resolvedConsultant?.booking_url;
    return typeof url === 'string' ? url.trim() : '';
  }, [resolvedConsultant]);

  const effectiveBookingUrl = advisorBookingUrl || consultantBookingUrl;

  const resolvedConsultantSlug = useMemo(() => {
    if (consultantSlug) return consultantSlug;
    if (resolvedConsultant?.slug) return resolvedConsultant.slug;
    if (slug && resolvedConsultant?.slug === slug) return slug;
    if (slug && !resolvedAdvisor && resolvedConsultant) return slug;
    if (slug && !resolvedAdvisor && !resolvedConsultant) return slug;
    return null;
  }, [consultantSlug, resolvedConsultant, resolvedAdvisor, slug]);

  const resolvedAdvisorSlug = useMemo(() => {
    if (advisorSlug) return advisorSlug;
    if (resolvedAdvisor?.slug) return resolvedAdvisor.slug;
    if (slug && resolvedAdvisor?.slug === slug) return slug;
    if (slug && resolvedAdvisor && !resolvedConsultant) return slug;
    return null;
  }, [advisorSlug, resolvedAdvisor, resolvedConsultant, slug]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!successMessage) return;

    const leadFormNode = leadFormRef.current;
    if (leadFormNode && typeof leadFormNode.scrollIntoView === 'function') {
      leadFormNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    const successNode = successAlertRef.current;
    if (successNode && typeof successNode.focus === 'function') {
      successNode.focus({ preventScroll: true });
    }
  }, [successMessage]);

  const createPublicLead = async () =>
    publicAxios.post('/public/leads', {
      lead: {
        name: form.name,
        email: form.email,
        phone: form.phone,
        organization_name: form.organization_name,
        role: form.role,
        source: 'landing',
      },
      consultant_slug: resolvedConsultantSlug,
      advisor_slug: resolvedAdvisorSlug,
      ...(activeToolKey ? { tool_key: activeToolKey } : {}),
    });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitLeadInFlightRef.current || isSubmitting || isStartingTour) return;
    submitLeadInFlightRef.current = true;
    setIsSubmitting(true);

    try {
      const response = await createPublicLead();
      setHasSubmittedLead(true);

      if (effectiveBookingUrl) {
        window.location.assign(effectiveBookingUrl);
        return;
      }

      if (response?.data?.assigned) {
        setSuccessMessage(
          tt('publicLead.messages.successAssigned', undefined, {
            advisor: advisorName || tt('publicLead.messages.yourConsultant'),
          }),
        );
      } else {
        setSuccessMessage(tt('publicLead.messages.successTeamReceived'));
      }
    } catch (error) {
      const status = error?.response?.status;
      const shouldToast = !error?.response || (status >= 500 && status <= 599);

      if (shouldToast) {
        toast({
          title: tt('publicLead.toast.submitError.title', 'Could not submit request'),
          description: tt('publicLead.toast.submitError.description', 'Please try again in a moment.'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      submitLeadInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleStartTour = async () => {
    if (startTourInFlightRef.current || isStartingTour) return;

    if (!form.name.trim() || !form.email.trim()) {
      toast({
        title: tt('publicLead.toast.missingDetails.title', 'Missing details'),
        description: tt('publicLead.toast.missingDetails.description', 'Please provide your name and email to start the tour.'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    startTourInFlightRef.current = true;
    setIsStartingTour(true);

    try {
      if (!hasSubmittedLead) {
        await createPublicLead();
        setHasSubmittedLead(true);
      }

      const result = await establishTourSession({
        name: form.name,
        email: form.email,
        phone: form.phone,
      });
      if (result?.ok) {
        navigate(result.redirectPath || '/app/organization');
      }
    } catch (error) {
      console.error('Tour start failed', error);
      toast({
        title: tt('publicLead.toast.startTourError.title', 'Could not start tour'),
        description: tt('publicLead.toast.startTourError.description', 'Please try again in a moment.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      startTourInFlightRef.current = false;
      setIsStartingTour(false);
    }
  };

  const scrollToLeadForm = (event) => {
    event.preventDefault();
    if (leadFormRef.current && typeof leadFormRef.current.scrollIntoView === 'function') {
      leadFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <Header />
      <Box bg="gray.50" minH="100vh" pt={{ base: 24, md: 28 }} pb={16}>
        <Container maxW="6xl">
          <Stack spacing={{ base: 10, md: 14 }}>
            <Box
              borderRadius="2xl"
              p={{ base: 6, md: 10 }}
              boxShadow="sm"
              borderWidth="1px"
              borderColor={activeTheme.borderColor}
              bgGradient={activeTheme.gradient}
            >
              <Stack spacing={5}>
                <Text
                  fontSize={{ base: 'sm', md: 'md' }}
                  fontWeight="semibold"
                  color={activeTheme.badgeColor}
                  letterSpacing="wide"
                >
                  {tt(activeToolConfig.hero.titleKey)}
                </Text>
                <Heading as="h1" size="xl" color="gray.800">
                  {tt(activeToolConfig.hero.subtitleKey)}
                </Heading>
                <Text
                  color="gray.700"
                  fontSize={{ base: 'md', md: 'lg' }}
                  dangerouslySetInnerHTML={{
                    __html: tt(activeToolConfig.hero.introHtmlKey),
                  }}
                />
                <Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
                  <Button
                    as="a"
                    href="#lead-form"
                    colorScheme={activeTheme.buttonColorScheme}
                    onClick={scrollToLeadForm}
                  >
                    {tt(activeToolConfig.hero.primaryCtaKey)}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    colorScheme={activeTheme.buttonColorScheme}
                    onClick={scrollToLeadForm}
                  >
                    {tt(activeToolConfig.hero.secondaryCtaKey)}
                  </Button>
                </Stack>
              </Stack>
            </Box>

            <Box bg="white" borderRadius="xl" p={{ base: 6, md: 8 }} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} alignItems="center">
                <Stack spacing={3}>
                  <Heading as="h2" size="md">
                    {tt(activeToolConfig.howItWorks.headingKey)}
                  </Heading>

                  {activeToolConfig.howItWorks.bulletKeys.map((key) => (
                    <HStack key={key} align="start" spacing={3}>
                      <Icon as={FiClipboard} color={activeTheme.accent} boxSize={5} mt={0.5} />
                      <Text color="gray.700">{tt(key)}</Text>
                    </HStack>
                  ))}

                  <Text color="gray.700" fontWeight="semibold">
                    {tt(activeToolConfig.howItWorks.footerEmphasisKey)}
                  </Text>
                </Stack>

                {isHowImageError ? (
                  <Box minH={{ base: '200px', md: '240px' }} borderRadius="lg" borderWidth="1px" borderColor="gray.100" bg="gray.50" />
                ) : (
                  <Image
                    src={activeToolConfig.howItWorks.image.src}
                    alt={tt(activeToolConfig.howItWorks.image.altKey)}
                    objectFit="cover"
                    borderRadius="lg"
                    minH={{ base: '200px', md: '240px' }}
                    w="100%"
                    onError={() => setIsHowImageError(true)}
                  />
                )}
              </SimpleGrid>
            </Box>

            <Box bg="white" borderRadius="xl" p={{ base: 4, md: 5 }} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                {activeToolConfig.valueStrip.map((item) => (
                  <HStack key={item.labelKey} spacing={3} align="start">
                    <Icon as={FiBarChart2} color={activeTheme.accent} boxSize={5} mt={0.5} />
                    <Text color="gray.700" fontWeight="medium">
                      {tt(item.labelKey)}
                    </Text>
                  </HStack>
                ))}
              </SimpleGrid>
            </Box>

            <Box bg="white" borderRadius="xl" p={{ base: 6, md: 8 }} boxShadow="sm" borderWidth="1px" borderColor="gray.100">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 10 }} alignItems="start">
                <Stack spacing={4}>
                  <Heading as="h2" size="md">
                    {tt(activeToolConfig.toolSection.headingKey)}
                  </Heading>
                  <Text
                    color="gray.700"
                    dangerouslySetInnerHTML={{
                      __html: tt(activeToolConfig.toolSection.descriptionHtmlKey),
                    }}
                  />

                  {activeToolConfig.toolSection.bulletKeys.map((key) => (
                    <HStack key={key} align="start" spacing={3}>
                      <Icon as={FiCompass} color={activeTheme.accent} boxSize={5} mt={0.5} />
                      <Text color="gray.700">{tt(key)}</Text>
                    </HStack>
                  ))}

                  <Text color="gray.700" mt={4}>
                    {tt(activeToolConfig.toolSection.subheadingKey)}
                  </Text>

                  {activeToolConfig.toolSection.subBulletKeys.map((key) => (
                    <HStack key={key} align="start" spacing={3}>
                      <Icon as={TbArrowBigRightLines} color={activeTheme.accent} boxSize={5} mt={0.5} />
                      <Text color="gray.700">{tt(key)}</Text>
                    </HStack>
                  ))}

                  <Text color="gray.700" fontWeight="semibold" pt={2}>
                    {tt(activeToolConfig.toolSection.summaryKey)}
                  </Text>
                </Stack>

                {!toolSectionMedia ? (
                  <Box minH={{ base: '220px', md: '100%' }} borderRadius="lg" borderWidth="1px" borderColor="gray.100" bg="gray.50" />
                ) : toolSectionMedia.type === 'video' ? (
                  isToolVideoError || !toolSectionMedia.embedUrl ? (
                    <Box minH={{ base: '220px', md: '100%' }} borderRadius="lg" borderWidth="1px" borderColor="gray.100" bg="gray.50" />
                  ) : (
                    <Box borderRadius="lg" overflow="hidden" minH={{ base: '220px', md: '100%' }}>
                      <Box position="relative" pt="56.25%" w="100%" borderRadius="lg" overflow="hidden">
                        <Box
                          as="iframe"
                          position="absolute"
                          top="0"
                          left="0"
                          w="100%"
                          h="100%"
                          border="0"
                          title="HKE video"
                          src={toolSectionMedia.embedUrl}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="strict-origin-when-cross-origin"
                          onError={() => setIsToolVideoError(true)}
                        />
                      </Box>
                    </Box>
                  )
                ) : isToolImageError ? (
                  <Box minH={{ base: '220px', md: '100%' }} borderRadius="lg" borderWidth="1px" borderColor="gray.100" bg="gray.50" />
                ) : (
                  <Image
                    src={toolSectionMedia.src}
                    alt={toolSectionMedia.altKey ? tt(toolSectionMedia.altKey) : ''}
                    objectFit="cover"
                    borderRadius="lg"
                    minH={{ base: '220px', md: '100%' }}
                    w="100%"
                    onError={() => setIsToolImageError(true)}
                  />
                )}
              </SimpleGrid>
            </Box>

            <Box
              borderRadius="xl"
              p={{ base: 6, md: 8 }}
              boxShadow="sm"
              borderWidth="1px"
              borderColor={activeTheme.borderColor}
              bgGradient={activeTheme.highlightGradient}
            >
              <Stack spacing={4}>
                <Heading as="h2" size="md">
                  {tt(activeToolConfig.why.headingKey)}
                </Heading>
                <Text color="gray.700">{tt(activeToolConfig.why.introKey)}</Text>

                {activeToolConfig.why.bulletKeys.map((key) => (
                  <HStack key={key} align="start" spacing={3}>
                    <Icon as={FiCheckCircle} color={activeTheme.accent} boxSize={5} mt={0.5} />
                    <Text color="gray.700">{tt(key)}</Text>
                  </HStack>
                ))}

                <Text color="gray.700" fontWeight="semibold" pt={2}>
                  {tt(activeToolConfig.why.summaryKey)}
                </Text>
              </Stack>
            </Box>

            <Box
              bgGradient={activeTheme.highlightGradient}
              borderRadius="xl"
              p={{ base: 6, md: 8 }}
              boxShadow="sm"
              borderWidth="1px"
              borderColor={activeTheme.borderColor}
            >
              <Stack spacing={3}>
                <Text color="gray.700" fontStyle="italic">
                  {tt(activeToolConfig.testimonial.quoteKey)}
                </Text>
                <Text fontSize="sm" fontWeight="semibold" color={activeToolConfig.theme.badgeColor}>
                  {tt(activeToolConfig.testimonial.attributionKey)}
                </Text>
              </Stack>
            </Box>

            <Box
              id="lead-form"
              ref={leadFormRef}
              bg="white"
              borderRadius="xl"
              p={{ base: 6, md: 8 }}
              boxShadow="md"
              borderWidth="1px"
              borderColor="gray.100"
            >
              <Stack spacing={4}>
                <Heading as="h2" size="md" textAlign={{ base: 'left', md: 'center' }}>
                  {tt(activeToolConfig.leadForm.finalCtaTitleKey)}
                </Heading>
                <Text color="gray.700" textAlign={{ base: 'left', md: 'center' }}>
                  {tt(activeToolConfig.leadForm.finalCtaBodyKey)}
                </Text>

                {referredByText && (
                  <Text fontSize="sm" color="gray.600">
                    {tt('publicLead.ui.referredBy')} {referredByText}
                  </Text>
                )}
                <Text color="gray.700" fontWeight="semibold">
                  {tt('publicLead.ui.contact')} {contactDetailText || titleName}
                </Text>

                {isLoadingConsultant && lookupSlug && (
                  <Stack direction="row" align="center" spacing={2}>
                    <Spinner size="sm" color="blue.500" />
                    <Text fontSize="sm" color="gray.500">
                      {tt('publicLead.ui.loadingConsultant')}
                    </Text>
                  </Stack>
                )}

                {successMessage ? (
                  <Stack spacing={2}>
                    <Alert
                      ref={successAlertRef}
                      status="success"
                      variant="left-accent"
                      alignItems="start"
                      borderRadius="md"
                      tabIndex={-1}
                    >
                      <AlertIcon mt={1} />
                      <Box>
                        <AlertTitle>{tt('publicLead.ui.requestSent')}</AlertTitle>
                        <AlertDescription>
                          {successMessage} {tt('publicLead.ui.weWillBeInTouch')}
                        </AlertDescription>
                      </Box>
                    </Alert>

                    <Flex align="center" gap={3} wrap="wrap" mt={4}>
                      <Button
                        type="button"
                        colorScheme={activeToolConfig.theme.buttonColorScheme}
                        isLoading={isStartingTour}
                        isDisabled={isStartingTour || isSubmitting}
                        onClick={handleStartTour}
                      >
                        {tt('publicLead.ui.startTour')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        colorScheme={activeToolConfig.theme.buttonColorScheme}
                        isDisabled={isStartingTour || isSubmitting}
                        onClick={() => {
                          setSuccessMessage('');
                          setHasSubmittedLead(false);
                        }}
                      >
                        {tt('publicLead.ui.submitAnotherRequest')}
                      </Button>
                    </Flex>
                  </Stack>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <Stack spacing={4}>
                      <FormControl isRequired>
                        <FormLabel>{tt('publicLead.form.nameLabel')}</FormLabel>
                        <Input
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          isDisabled={isSubmitting || isStartingTour}
                          placeholder={tt('publicLead.form.namePlaceholder')}
                        />
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel>{tt('publicLead.form.emailLabel')}</FormLabel>
                        <Input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          isDisabled={isSubmitting || isStartingTour}
                          placeholder={tt('publicLead.form.emailPlaceholder')}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>{tt('publicLead.form.phoneLabel')}</FormLabel>
                        <Input
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          isDisabled={isSubmitting || isStartingTour}
                          placeholder={tt('publicLead.form.phonePlaceholder')}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>{tt('publicLead.form.organizationLabel')}</FormLabel>
                        <Input
                          name="organization_name"
                          value={form.organization_name}
                          onChange={handleChange}
                          isDisabled={isSubmitting || isStartingTour}
                          placeholder={tt('publicLead.form.organizationPlaceholder')}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>{tt('publicLead.form.roleLabel')}</FormLabel>
                        <Input
                          name="role"
                          value={form.role}
                          onChange={handleChange}
                          isDisabled={isSubmitting || isStartingTour}
                          placeholder={tt('publicLead.form.rolePlaceholder')}
                        />
                      </FormControl>

                      <Flex align="center">
                        <Button
                          type="submit"
                          colorScheme={activeToolConfig.theme.buttonColorScheme}
                          isLoading={isSubmitting}
                          isDisabled={isSubmitting || isStartingTour}
                        >
                    {tt(activeToolConfig.hero.primaryCtaKey)}
                        </Button>
                        <Spacer />
                        <Button
                          type="button"
                          variant="outline"
                          colorScheme={activeToolConfig.theme.buttonColorScheme}
                          isLoading={isStartingTour}
                          isDisabled={isSubmitting || isStartingTour}
                          onClick={handleStartTour}
                        >
                          {tt(activeToolConfig.hero.secondaryCtaKey)}
                        </Button>
                      </Flex>

                      <Text fontSize="sm" color="gray.600">
                        {tt(activeToolConfig.leadForm.footerLineKey)}
                      </Text>
                      <HStack spacing={2}>
                        <Icon as={FiShield} color="gray.500" boxSize={4} />
                        <Text fontSize="sm" color="gray.600">
                          {tt('publicLead.ui.consent')}
                        </Text>
                      </HStack>
                    </Stack>
                  </form>
                )}
              </Stack>
            </Box>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export { isYouTubeUrl, toYouTubeEmbedUrl, resolveMedia };
export default PublicConsultantLead;
